import { api, apiWithoutContentType } from './client';

// Students in the coach's assigned Balagruha(s), with Balagruha info.
// GET /api/v2/lms/coach/:coachId/students -> { success, balagruhas, count, data: [students] }
export const getCoachStudents = async (coachId) => {
  const response = await api.get(`/api/v2/lms/coach/${coachId}/students`);
  return response.data;
};

// Learning Zone sidebar task type rows: published courses' task types by category, each with its chapters and items.
// GET /api/v2/lms/student/:studentId/courses/learning-zone -> { success, categories: [{ name, taskTypes: [...] }] }
export const getLearningZoneCourses = async (studentId) => {
  const response = await api.get(`/api/v2/lms/student/${studentId}/courses/learning-zone`);
  return response.data;
};

// Per-student course progress dashboard (same endpoint the student dashboard uses).
// GET /api/v2/lms/student/:studentId/dashboard -> { success, data: { studentName, courses, lastActivity, stats } }
export const getStudentDashboard = async (studentId) => {
  const response = await api.get(`/api/v2/lms/student/${studentId}/dashboard`);
  return response.data;
};

// Syllabus Tracker: per-child score matrix for a Balagruha/Topic/Month/Year. `subcategories`
// (a subcategory's view, utils/syllabusTrackerScopes.js) keeps only their columns.
// GET /api/v2/lms/coach/syllabus-tracker/matrix -> { success, data: { sessionCount, children: [...] } }
export const getSyllabusMatrix = async ({ balagruhaId, topic, month, year, subcategories, courseId }) => {
  const response = await api.get('/api/v2/lms/coach/syllabus-tracker/matrix', {
    params: {
      balagruhaId,
      topic,
      month,
      year,
      ...(subcategories?.length ? { subcategories: subcategories.join(',') } : {}),
      ...(courseId ? { courseId } : {}),
    },
  });
  return response.data;
};

// Syllabus Tracker: the admin's course categories and Courses subcategories for its Category ›
// Subcategory pickers.
// GET /api/v2/lms/coach/syllabus-tracker/subcategories -> { success, data: [subcategories], taskTypes, categories }
export const getSyllabusSubcategories = async () => {
  const response = await api.get('/api/v2/lms/coach/syllabus-tracker/subcategories');
  return response.data;
};

// Syllabus Tracker: upsert one child's session scores.
// PUT /api/v2/lms/coach/syllabus-tracker/entries -> { success, data: { sessionCount, child } }
export const saveSyllabusEntries = async ({ balagruhaId, topic, month, year, studentId, entries }) => {
  const response = await api.put('/api/v2/lms/coach/syllabus-tracker/entries', {
    balagruhaId,
    topic,
    month,
    year,
    studentId,
    entries,
  });
  return response.data;
};

// Syllabus Tracker: clear one child's entry (score/N-A/comment) for a session.
// DELETE /api/v2/lms/coach/syllabus-tracker/entries -> { success, data: { sessionCount, child } }
export const deleteSyllabusEntry = async ({ balagruhaId, topic, month, year, studentId, sessionNumber }) => {
  const response = await api.delete('/api/v2/lms/coach/syllabus-tracker/entries', {
    data: { balagruhaId, topic, month, year, studentId, sessionNumber },
  });
  return response.data;
};

// Syllabus Tracker: the child refused the activity the admin's lesson plan gave them on a visit
// (`date` + topic + sessionNumber) and did `doneSessionNumber` (named `doneName`) instead, or nothing.
// PUT /api/v2/lms/coach/syllabus-tracker/plan-refusals -> { success, data: refusal }
export const savePlanRefusal = async ({ balagruhaId, studentId, date, topic, sessionNumber, doneSessionNumber, doneName, reason }) => {
  const response = await api.put('/api/v2/lms/coach/syllabus-tracker/plan-refusals', {
    balagruhaId,
    studentId,
    date,
    topic,
    sessionNumber,
    doneSessionNumber: doneSessionNumber ?? null,
    doneName: doneName || '',
    reason: reason || '',
  });
  return response.data;
};

// Syllabus Tracker: undo that (the child did the planned activity after all).
// DELETE /api/v2/lms/coach/syllabus-tracker/plan-refusals -> { success }
export const deletePlanRefusal = async ({ balagruhaId, studentId, date, topic, sessionNumber }) => {
  const response = await api.delete('/api/v2/lms/coach/syllabus-tracker/plan-refusals', {
    data: { balagruhaId, studentId, date, topic, sessionNumber },
  });
  return response.data;
};

// The refusals coaches recorded for a visit day (Daily Schedule › View).
// GET /api/v2/lms/coach/syllabus-tracker/plan-refusals -> { success, data: [refusals] }
export const getPlanRefusals = async ({ balagruhaId, date }) => {
  const response = await api.get('/api/v2/lms/coach/syllabus-tracker/plan-refusals', { params: { balagruhaId, date } });
  return response.data;
};

// Syllabus Tracker: this child's "Overall Coach Remarks" note for a Topic/Month/Year.
// GET /api/v2/lms/coach/syllabus-tracker/remark -> { success, data: { remarks, updatedAt } }
export const getSyllabusRemark = async ({ balagruhaId, topic, month, year, studentId }) => {
  const response = await api.get('/api/v2/lms/coach/syllabus-tracker/remark', {
    params: { balagruhaId, topic, month, year, studentId },
  });
  return response.data;
};

// Syllabus Tracker: save this child's "Overall Coach Remarks" note.
// PUT /api/v2/lms/coach/syllabus-tracker/remark -> { success, data: { remarks, updatedAt } }
export const saveSyllabusRemark = async ({ balagruhaId, topic, month, year, studentId, remarks }) => {
  const response = await api.put('/api/v2/lms/coach/syllabus-tracker/remark', {
    balagruhaId,
    topic,
    month,
    year,
    studentId,
    remarks,
  });
  return response.data;
};

// Syllabus Tracker: one child's practice submission, with a presigned media URL,
// for the "Submissions" filter's review-and-grade modal.
// GET /api/v2/lms/coach/syllabus-tracker/submissions/:id -> { success, data: { submission } }
export const getSyllabusSubmission = async (submissionId) => {
  const response = await api.get(`/api/v2/lms/coach/syllabus-tracker/submissions/${submissionId}`);
  return response.data;
};

// Syllabus Tracker: "Save Grade" on a submission — writes score/comment through
// to the matching SyllabusEntry and marks the submission reviewed. `ruleScores`
// (only for an activity with coin rules) is their part of the score.
// PUT /api/v2/lms/coach/syllabus-tracker/submissions/:id/grade -> { success, data: { sessionCount, child } }
export const gradeSyllabusSubmission = async (submissionId, { score, comment, ruleScores }) => {
  const response = await api.put(`/api/v2/lms/coach/syllabus-tracker/submissions/${submissionId}/grade`, {
    score,
    comment,
    ...(ruleScores ? { ruleScores } : {}),
  });
  return response.data;
};

// Syllabus Tracker "Practice record": the coach's audio / video of a child's practice for one cell
// (`file`, recorded in the browser), saved to S3 as one of the child's submissions for it; the same
// route the Coins app uses. A video can take a while to send, so not the shared client's 30 s.
// POST /api/v2/lms/coach/coins/syllabus/children/:studentId/sessions/:sessionNumber/recording
//   (multipart: file, submissionType, balagruhaId, topic, month, year) -> { success, submission: { _id, ... } }
const RECORDING_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;
export const recordSyllabusSession = async (studentId, sessionNumber, { file, submissionType, balagruhaId, topic, month, year }) => {
  const form = new FormData();
  Object.entries({ balagruhaId, topic, month, year, submissionType }).forEach(([name, value]) => form.append(name, String(value)));
  form.append('file', file);
  const response = await apiWithoutContentType.post(
    `/api/v2/lms/coach/coins/syllabus/children/${studentId}/sessions/${sessionNumber}/recording`,
    form,
    { timeout: RECORDING_UPLOAD_TIMEOUT_MS }
  );
  return response.data;
};
